import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import {
  clearSessionItem,
  readSessionItemWithLocalFallback,
  writeSessionItem,
} from '@/lib/session-storage'
import styles from './promoter.module.css'

export const Route = createFileRoute('/promoter')({
  component: PromoterRoute,
})

const PROMOTER_TOKEN_KEY = 'statureboost_promoter_token_v1'

function readStoredPromoterToken(): string {
  return readSessionItemWithLocalFallback(PROMOTER_TOKEN_KEY) ?? ''
}

function PromoterRoute() {
  const [email, setEmail] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [token, setToken] = useState(readStoredPromoterToken)
  const [profile, setProfile] = useState<Awaited<ReturnType<typeof api.promoterMe>> | null>(null)
  const [credits, setCredits] = useState<Awaited<ReturnType<typeof api.promoterCredits>> | null>(
    null
  )
  const [campaignName, setCampaignName] = useState('launch-sprint')
  const [utmCampaign, setUtmCampaign] = useState('launch_sprint')
  const [latestLink, setLatestLink] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const isBlockedForLinkCreation = useMemo(() => {
    if (!profile || !credits) {
      return false
    }

    if (profile.status === 'suspended' || profile.status === 'revoked') {
      return true
    }

    return credits.balance <= 0
  }, [profile, credits])

  const loadWorkspace = async (promoterToken: string): Promise<void> => {
    setIsBusy(true)
    setError(null)

    try {
      const [nextProfile, nextCredits] = await Promise.all([
        api.promoterMe(promoterToken),
        api.promoterCredits(promoterToken),
      ])
      setProfile(nextProfile)
      setCredits(nextCredits)
    } catch (loadError) {
      setError(
        loadError instanceof Error ? loadError.message : 'Failed to load promoter workspace.'
      )
    } finally {
      setIsBusy(false)
    }
  }

  useEffect(() => {
    if (!token) {
      return
    }

    void loadWorkspace(token)
  }, [token])

  const handleLogin = async (): Promise<void> => {
    if (email.trim().length === 0 || inviteCode.trim().length === 0) {
      setError('Email and invite code are required.')
      return
    }

    setIsBusy(true)
    setError(null)
    setNotice(null)

    try {
      const result = await api.promoterLogin({
        email: email.trim(),
        inviteCode: inviteCode.trim(),
      })

      writeSessionItem(PROMOTER_TOKEN_KEY, result.session.accessToken)
      setToken(result.session.accessToken)
      setNotice('Promoter session created.')
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : 'Promoter login failed.')
    } finally {
      setIsBusy(false)
    }
  }

  const handleCreateLink = async (): Promise<void> => {
    if (!token) {
      return
    }

    setIsBusy(true)
    setError(null)
    setNotice(null)

    try {
      const created = await api.promoterCreateLink(token, {
        landingPath: '/',
        campaignName,
        utmSource: 'promoter',
        utmMedium: 'social',
        utmCampaign,
      })

      setLatestLink(created.link.url)
      setNotice(`Link created. Balance now ${created.balance}.`)
      await loadWorkspace(token)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Link creation failed.')
    } finally {
      setIsBusy(false)
    }
  }

  const handleLogout = (): void => {
    clearSessionItem(PROMOTER_TOKEN_KEY)
    setToken('')
    setProfile(null)
    setCredits(null)
    setLatestLink(null)
    setNotice('Promoter session cleared.')
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>Promoter Workspace</h1>
        <p className={styles.subtitle}>
          Minimal login, visible balance, and one-click link creation with automatic credit debit.
        </p>
      </header>

      {!token && (
        <section className={styles.card}>
          <h2>Promoter Login</h2>
          <div className={styles.row}>
            <input
              className={styles.input}
              type="email"
              placeholder="Promoter email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="Invite code"
              value={inviteCode}
              onChange={(event) => setInviteCode(event.target.value)}
            />
            <button
              className={styles.button}
              type="button"
              onClick={() => void handleLogin()}
              disabled={isBusy}
            >
              Sign in
            </button>
          </div>
        </section>
      )}

      {token && (
        <>
          <section className={styles.card}>
            <div className={styles.row}>
              <button className={styles.secondaryButton} type="button" onClick={handleLogout}>
                Clear session
              </button>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => {
                  void loadWorkspace(token)
                }}
                disabled={isBusy}
              >
                Refresh
              </button>
            </div>
            <p className={styles.muted}>
              {profile ? `${profile.name} (${profile.email})` : 'Loading profile...'}{' '}
              {profile ? `• status: ${profile.status}` : ''}
            </p>
            <p className={styles.muted}>
              {credits
                ? `Balance ${credits.balance}, next refresh ${new Date(credits.nextRefreshAt).toLocaleString()}`
                : 'Loading credits...'}
            </p>
          </section>

          <section className={styles.card}>
            <h2>Create Campaign Link</h2>
            {profile?.status === 'suspended' && (
              <p className={styles.error}>Account Suspended. Contact support to continue.</p>
            )}
            {profile?.status === 'revoked' && (
              <p className={styles.error}>Account Revoked. Contact support to continue.</p>
            )}
            {credits && credits.balance <= 0 && (
              <p className={styles.error}>
                No Credits Available. Next refresh at{' '}
                {new Date(credits.nextRefreshAt).toLocaleString()}.
              </p>
            )}
            <div className={styles.row}>
              <input
                className={styles.input}
                type="text"
                value={campaignName}
                onChange={(event) => setCampaignName(event.target.value)}
                placeholder="Campaign name"
              />
              <input
                className={styles.input}
                type="text"
                value={utmCampaign}
                onChange={(event) => setUtmCampaign(event.target.value)}
                placeholder="utm_campaign"
              />
              <button
                className={styles.button}
                type="button"
                onClick={() => void handleCreateLink()}
                disabled={isBusy || isBlockedForLinkCreation}
              >
                Create link
              </button>
            </div>
            {latestLink && <p className={styles.notice}>Latest link: {latestLink}</p>}
          </section>

          {credits && (
            <section className={styles.card}>
              <h2>Credit Ledger</h2>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Delta</th>
                    <th>Balance</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.ledger.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.eventType}</td>
                      <td>{entry.delta}</td>
                      <td>{entry.balanceAfter}</td>
                      <td>{entry.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {notice && <p className={styles.notice}>{notice}</p>}
      {error && <p className={styles.error}>{error}</p>}
    </section>
  )
}
